import { store } from "../store/store"
import { logout, setNewTokens } from "../slices/authSlice"
import { createApiError } from "./formatApiError"
import { API_BASE_URL } from "../config/apiBaseUrl"

const API_URL = API_BASE_URL

let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  const state = store.getState().auth
  const refreshToken = state.refreshToken

  if (!refreshToken) {
    throw new Error("No refresh token")
  }

  const response = await fetch(`${API_URL}/api/users/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  })

  if (!response.ok) {
    throw new Error("Refresh failed")
  }

  const data = await response.json()

  store.dispatch(
    setNewTokens({
      token: data.accessToken,
      refreshToken: data.refreshToken
    })
  )

  return data.accessToken
}

// responseType "blob" returns the body as a file (a PDF to download); by
// default JSON is parsed and anything else comes back as text.
export async function apiFetch(
  endpoint: string,
  options: RequestInit = {},
  responseType: "auto" | "blob" = "auto"
) {
  const headers: HeadersInit = options.headers || {}

  let token = store.getState().auth.token

  if (token) {
    (headers as any)["Authorization"] = `Bearer ${token}`
  }

  let res: Response

  try {
    res = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers
    })
  } catch (error) {
    console.error("API request failed:", error)
    throw new Error(
      "The server could not be reached. Please check your connection, reload the page and try again."
    )
  }

  if (res.status === 401) {
    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken()
      }

      token = await refreshPromise
      refreshPromise = null

      ;(headers as any)["Authorization"] = `Bearer ${token}`

      try {
        res = await fetch(`${API_URL}${endpoint}`, {
          ...options,
          headers
        })
      } catch (error) {
        console.error("API retry failed:", error)
        throw new Error(
          "The server could not be reached. Please check your connection, reload the page and try again."
        )
      }

    } catch {
      refreshPromise = null
      store.dispatch(logout())
      window.location.href = "/login"
      return
    }
  }

  if (!res.ok) {
    const text = await res.text()
    const error = createApiError(res.status, text)
    console.error("API response error:", {
      endpoint,
      status: res.status,
      technicalMessage: (error as any).technicalMessage
    })
    throw error
  }

  if (responseType === "blob") {
    return res.blob()
  }

  const contentType = res.headers.get("Content-Type") || ""
  if (contentType.includes("application/json")) {
    return res.json()
  }

  return res.text()
}
